import { PROJECTILE_FLIGHT_SECONDS } from "./world";
// Game configuration - single source of truth for all tunables

export interface GameConfig {
  // Round durations (seconds)
  roundGatherDuration: number;
  roundRollDuration: number;
  roundThrowDuration: number;
  bannerDuration: number;
  countdownDuration: number;
  suddenDeathDuration: number;

  // Combat
  maxHP: number;
  ballDamage: number; // every snowball does this much damage
  perfectBonus: number; // extra damage for a PERFECT PACK ball
  perfectMaxSeconds: number; // a closed, round lap at most this long is perfect

  // Gather (R1)
  snowPerStroke: number;
  snowCap: number; // maximum snow the jar holds

  // Roll (R2): each closed lap packs one ball out of snowPerBall snow
  snowPerBall: number;
  carrySnowCap: number; // leftover snow carried into the next match (max)
  carrySnow: number; // snow carried in from the previous match

  // Opponent ladder (1-based index into SNOWMEN); see withSnowman().
  snowmanLevel: number;
  opponentHP?: number; // opponent max HP (defaults to maxHP)
  botDodgeChance?: number; // chance the bot dodges a landed player ball
  botBalls?: number; // ball budget the bot builds in R1/R2

  /** First-run practice match: untimed, scripted, no fail state. */
  tutorial?: boolean;

  // Throw (R3)
  telegraphDuration: number; // windup before projectile flies
  projectileFlightTime: number; // seconds to reach target

  // Dodge/Block (not fully implemented in M1)
  dodgeIFramesDuration: number;
  dodgeCooldown: number;
  blockWindowDuration: number;
  blockCooldown: number;
  perfectBlockWindow: number;

  // Seeded bot behavior
  bot: {
    gatherInterval: number;
    rollInterval: number;
    throwInterval: number;
    reactionMin: number;
    reactionMax: number;
    accuracy: number;
  };

  // Clock
  tickRate: number; // Hz
}

export const DEFAULT_CONFIG: GameConfig = {
  // Round durations
  roundGatherDuration: 10,
  roundRollDuration: 10,
  roundThrowDuration: 20,
  bannerDuration: 2,
  countdownDuration: 3,
  suddenDeathDuration: 10,

  // Combat
  maxHP: 100,
  ballDamage: 10,
  perfectBonus: 5,
  perfectMaxSeconds: 0.9,

  // Gather
  snowPerStroke: 2,
  snowCap: 120,

  // Roll
  snowPerBall: 10,
  carrySnowCap: 20,
  carrySnow: 0,

  snowmanLevel: 1,

  // Throw
  telegraphDuration: 0.5,
  projectileFlightTime: PROJECTILE_FLIGHT_SECONDS,

  // Dodge/Block
  dodgeIFramesDuration: 0.4,
  dodgeCooldown: 2.0,
  blockWindowDuration: 0.6,
  blockCooldown: 3.0,
  perfectBlockWindow: 0.15,

  bot: {
    gatherInterval: 0.18,
    rollInterval: 0.4,
    throwInterval: 1.5,
    reactionMin: 0.1,
    reactionMax: 0.35,
    accuracy: 0.6,
  },

  // Clock
  tickRate: 60,
};

// Projectile travel: speed (units/s) maps to flight time over a fixed 5.8 unit
// lane. Speed 8 equals the original PROJECTILE_FLIGHT_SECONDS (0.725 s).
export const PROJECTILE_LANE_UNITS = 5.8;
export function flightSecondsForSpeed(speed: number): number {
  return PROJECTILE_LANE_UNITS / speed;
}

export interface SnowmanSpec {
  level: number; // 1-based
  name: string;
  hp: number;
  throwInterval: number; // seconds between bot throws
  speed: number; // projectile speed, u/s
  blockWindowMs: number; // the player's block window against this snowman
  dodgeWindowMs: number; // the player's dodge window against this snowman
  botDodge: number; // chance the snowman dodges a landed player ball
  botBalls: number; // balls the bot packs (same economy as the player)
  accuracy: number; // chance a bot throw is aimed at the player's lane
}

// Difficulty ladder, sim-checked (scripts/sim.ts). Speed, windows and dodge
// come from Game Design; HP, interval, ball budget and accuracy are retuned.
export const SNOWMEN: readonly SnowmanSpec[] = [
  {
    level: 1,
    name: "Mika",
    hp: 60,
    throwInterval: 1.8,
    speed: 6,
    blockWindowMs: 800,
    dodgeWindowMs: 500,
    botDodge: 0,
    botBalls: 11,
    accuracy: 0.66,
  },
  {
    level: 2,
    name: "Pip",
    hp: 70,
    throwInterval: 1.6,
    speed: 7,
    blockWindowMs: 700,
    dodgeWindowMs: 450,
    botDodge: 0.05,
    botBalls: 12,
    accuracy: 0.68,
  },
  {
    level: 3,
    name: "Juniper",
    hp: 80,
    throwInterval: 1.5,
    speed: 8,
    blockWindowMs: 600,
    dodgeWindowMs: 400,
    botDodge: 0.1,
    botBalls: 13,
    accuracy: 0.7,
  },
  {
    level: 4,
    name: "Biscuit",
    hp: 85,
    throwInterval: 1.4,
    speed: 9,
    blockWindowMs: 500,
    dodgeWindowMs: 350,
    botDodge: 0.15,
    botBalls: 14,
    accuracy: 0.72,
  },
  {
    level: 5,
    name: "Big Flurry",
    hp: 95,
    throwInterval: 1.3,
    speed: 10,
    blockWindowMs: 420,
    dodgeWindowMs: 300,
    botDodge: 0.2,
    botBalls: 15,
    accuracy: 0.74,
  },
];

export function snowmanSpec(level: number): SnowmanSpec {
  const index = Math.min(
    SNOWMEN.length,
    Math.max(1, Math.floor(Number.isFinite(level) ? level : 1)),
  );
  return SNOWMEN[index - 1]!;
}

/**
 * Config for fighting the given snowman. The bot builds the same economy as
 * the player: botBalls x 10 snow in R1 (5 strokes per ball) and one lap per
 * ball in R2, spread over the round with a 1.5 s margin at the end.
 */
export function withSnowman(base: GameConfig, level: number): GameConfig {
  const spec = snowmanSpec(level);
  const strokes = spec.botBalls * (base.snowPerBall / base.snowPerStroke);
  return {
    ...base,
    snowmanLevel: spec.level,
    opponentHP: spec.hp,
    botDodgeChance: spec.botDodge,
    botBalls: spec.botBalls,
    projectileFlightTime: flightSecondsForSpeed(spec.speed),
    blockWindowDuration: spec.blockWindowMs / 1000,
    dodgeIFramesDuration: spec.dodgeWindowMs / 1000,
    bot: {
      ...base.bot,
      throwInterval: spec.throwInterval,
      accuracy: spec.accuracy,
      gatherInterval: (base.roundGatherDuration - 1.5) / strokes,
      rollInterval: (base.roundRollDuration - 1.5) / spec.botBalls,
    },
  };
}

/** Snow to scoop in tutorial step 1, and balls to pack in step 2. */
export const TUTORIAL_SNOW = 40;
export const TUTORIAL_BALLS = 4;

/**
 * Practice match: Mika with 30 HP who throws one slow ball (1.2 s flight,
 * 1000 ms to block or dodge). Rounds end on success rather than a clock.
 */
export function tutorialConfig(base: GameConfig): GameConfig {
  return {
    ...withSnowman(base, 1),
    tutorial: true,
    opponentHP: 30,
    botDodgeChance: 0,
    projectileFlightTime: 1.2,
    blockWindowDuration: 1.0,
    dodgeIFramesDuration: 1.0,
    bot: { ...base.bot, accuracy: 1 },
  };
}
